import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Panel, Table } from 'react-bootstrap';
import { inject } from '../../../utils/decorators';
import { ApiStore } from '../../../stores/ApiStore';
import { UiStore } from '../../../stores/UiStore';
import Callout from '../../site/Callout';
import { ISesErgebnisBlockSystemneigung } from '../../../models/ApiTypesSes';

interface ISesAnzeigeSystemneigungProps {
  vorgangId: string;
}

/** Zeigt die SES Systemneigungen des Gesamtsystems inkl. ihrer Beurteilung für abgeschlossene Auswertungen an */
@observer
export default class SesAnzeigeSystemneigung extends React.Component<ISesAnzeigeSystemneigungProps, {}> {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(UiStore) uiStore: UiStore;

  @observable ergebnisBlock: ISesErgebnisBlockSystemneigung;

  async componentDidMount() {
    const response = await this.apiStore.ergebnisBlockSes(this.props.vorgangId);
    if (response.ok) {
      if (response.json) {
        this.setErgebnisBlock(response.json);
      } else {
        this.clearErgebnisBlock();
      }
    } else {
      this.clearErgebnisBlock();
    }
  }

  @action setErgebnisBlock(ergebnisBlock: ISesErgebnisBlockSystemneigung) {
    this.ergebnisBlock = ergebnisBlock;
  }

  @action clearErgebnisBlock() {
    this.ergebnisBlock.datenvorhanden = false;
  }

  render() {
    if (this.ergebnisBlock && this.ergebnisBlock.datenvorhanden) {
      let beurteilungStyle: 'success' | 'danger' | 'warning' | 'info';
      var beurteilungText;
      let korrekturWertSEPohneNA;
      let korrekturWertSEPmitNA;
      let laengsneigungSEPio;
      let querneigungSEPio;
      let flaecheio;
      let neigungenio;
      let beurteilungio;

      // Korrekturwerte SEP für Ausführungen mit und ohne Niveauausgleich (NA) selektieren
      korrekturWertSEPohneNA = (this.ergebnisBlock.fahrzeug.laengs - this.ergebnisBlock.sep.quer).toLocaleString(undefined, {minimumFractionDigits: 1});
      korrekturWertSEPmitNA = this.ergebnisBlock.fahrzeug.laengs.toLocaleString(undefined, {minimumFractionDigits: 1});
      // Überprüfung der Gleichrichtiung der Flächen (nur für 2014er Rili)
      if (this.ergebnisBlock.richtlinie === '2014') {
        laengsneigungSEPio = ((this.ergebnisBlock.fahrzeug.laengs * this.ergebnisBlock.sep.quer) >= 0.0) ? true : false;
        querneigungSEPio = ((this.ergebnisBlock.fahrzeug.quer * this.ergebnisBlock.sep.laengs) >= 0.0) ? true : false;
      } else {
        laengsneigungSEPio = true;
        querneigungSEPio = true;
      }

      /*
        Beurteilung der Neigungen der Flächen zueinander

        Fläche in Ordnung wenn beide Kreuzvergleiche mit gleichem Vorzeichen
      */
      flaecheio = laengsneigungSEPio && querneigungSEPio;
      if (flaecheio) {
        beurteilungStyle = 'success';
        beurteilungText = (
          <span>
            Korrekturwert für SEP <b>ohne Niveauausgleich</b>: <b>{korrekturWertSEPohneNA}%</b><br/>
            Korrekturwert für SEP <b>mit Niveauausgleich</b>: <b>{korrekturWertSEPmitNA}%</b>
          </span>
        );
      } else {
        beurteilungStyle = 'danger';
        beurteilungText = (<span>Aufgrund der entgegengesetzten Neigungen Fahrzeug-Aufstellfläche zu SEP Aufstellfläche erfüllt diese Fläche die Anforderungen nicht.<br/><b>Eine Korrektur des SEPs ist daher nicht möglich!</b></span>);
      }

      /*
        Beurteilung der einzelnen Neigungen

        Alle Neigungen müssen im Bereich ±1,5% liegen
      */
      let maxNeigung = 1.5;
      neigungenio = (
        (Math.abs(this.ergebnisBlock.fahrzeug.laengs) <= maxNeigung) && (Math.abs(this.ergebnisBlock.fahrzeug.quer) <= maxNeigung)
        &&
        (Math.abs(this.ergebnisBlock.sep.laengs) <= maxNeigung) && (Math.abs(this.ergebnisBlock.sep.quer) <= maxNeigung)
      );
      if (flaecheio && !neigungenio) {
        beurteilungStyle = 'danger';
        beurteilungText = (<span>Aufgrund der zu großen Neigungen (außerhalb von ±1,5%) erfüllt diese Fläche die Anforderungen nicht.<br/><b>Eine Korrektur des SEPs ist daher nicht möglich!</b></span>);
      }

      beurteilungio = flaecheio && neigungenio;

      return (
        <Panel header="Systemneigung">
          <Callout bsStyle="info">
            Folgende Systemneigung wurde im Rahmen der Auswertung ermittelt.<br/>
            Details entnehmen Sie bitte den entsprechenden Dokumenten.
          </Callout>
          <Table striped={false} bordered={true} condensed={false} hover={false} className="auswertung-systemneigung">
            <thead>
              <tr>
                <th width="20%"/>
                <th width="40%">Fahrzeug</th>
                <th width="40%">SEP</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Längs</th>
                <td className={(laengsneigungSEPio && Math.abs(this.ergebnisBlock.fahrzeug.laengs) <= maxNeigung) ? 'value-success' : 'value-error'}>
                  {this.ergebnisBlock.fahrzeug.laengs.toLocaleString(undefined, {minimumFractionDigits: 1})}%
                </td>
                <td className={(querneigungSEPio && Math.abs(this.ergebnisBlock.sep.laengs) <= maxNeigung) ? 'value-success' : 'value-error'}>
                  {this.ergebnisBlock.sep.laengs.toLocaleString(undefined, {minimumFractionDigits: 1})}%
                </td>
              </tr>
              <tr>
                <th>Quer</th>
                <td className={(querneigungSEPio && Math.abs(this.ergebnisBlock.fahrzeug.quer) <= maxNeigung) ? 'value-success' : 'value-error'}>
                  {this.ergebnisBlock.fahrzeug.quer.toLocaleString(undefined, {minimumFractionDigits: 1})}%
                </td>
                <td className={(laengsneigungSEPio && Math.abs(this.ergebnisBlock.sep.quer) <= maxNeigung) ? 'value-success' : 'value-error'}>
                  {this.ergebnisBlock.sep.quer.toLocaleString(undefined, {minimumFractionDigits: 1})}%
                </td>
              </tr>
            </tbody>
          </Table>

          <Callout bsStyle={beurteilungStyle}>
            {beurteilungText}
            {(beurteilungio) && (<div>Einzustellen an der Laser-Kalibriervorrichtung</div>)}
          </Callout>

        </Panel>
      );
    } else {
      return <div/>;
    }
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/ses/SesAnzeigeSystemneigung.tsx